import React, { useMemo, useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { usePreferences } from '../context/UserPreferencesContext';
import MediaPlayer from '../components/media/MediaPlayer';
import WordSelector from '../components/annotate/WordSelector';
import AnnotationForm from '../components/annotate/AnnotationForm';
import AddPanel from '../components/annotate/AddPanel';
import BunchList from '../components/annotate/BunchList';
import CurrentMoment from '../components/annotate/CurrentMoment';
import TitlePanel from '../components/annotate/TitlePanel';
import useVideoContent from '../components/annotate/useVideoContent';
import { TimelineProvider } from '../components/annotate/timelineContext';
import { useSectionOrder, MoveSection } from '../components/annotate/sectionOrder';
import useYasna from '../yasna/useYasna';
import { DEFAULT_MEDIA_URL } from '../config/defaultMedia';
import './PageShell.css';
import './AnnotatePage.css';

/**
 * AnnotatePage — the teacher's annotation workflow.
 * Flow: Load video → pick a word → Link to a yasna sense → Save annotation.
 * A word is picked either by typing it at a moment in the video (no
 * subtitles needed; the Add panel searches yasna and saves in one step)
 * or by tapping it in a subtitle (AnnotationForm).
 */
function AnnotatePage() {
  const { targetLanguage } = usePreferences();
  const { yasna, error: yasnaError } = useYasna();

  // The order of the Add panel and the Subtitles & annotations list.
  const [order, moveSection] = useSectionOrder();

  // Video state
  const [videoId, setVideoId] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [currentTime, setCurrentTime] = useState(0);

  // The video's subtitles and annotations, shared by the Now panel, the Add
  // panel and the lists.
  const content = useVideoContent(videoId);
  const onSubtitleEdited = (updated, moved, neighbours = []) => {
    content.upsertSubtitle(updated);
    // The next subtitle, when its start followed this one's end
    neighbours.forEach(content.upsertSubtitle);
    if (moved) content.reloadAnnotations();
  };
  const onSubtitleDeleted = () => content.reloadSubtitles();
  const timeline = useMemo(
    () => ({ subtitles: content.subtitles, annotations: content.annotations }),
    [content.subtitles, content.annotations]
  );

  // Subtitle state
  const [selectedSubtitle, setSelectedSubtitle] = useState(null);

  // Annotation state
  const [annotationMode, setAnnotationMode] = useState(false); // showing annotation form
  const [surfaceText, setSurfaceText] = useState('');
  const [wordIndices, setWordIndices] = useState([]);
  const [lastSavedAnnotation, setLastSavedAnnotation] = useState(null);
  const [wordSelectorKey, setWordSelectorKey] = useState(0); // remount WordSelector to reset selection

  // --- Video handlers ---
  const handleVideoLoad = async (id, title) => {
    setVideoId(id);
    setVideoTitle(title || '');
    setSelectedSubtitle(null);
    setAnnotationMode(false);
    setLastSavedAnnotation(null);

    // Create or update videos document in Firestore
    try {
      const videoRef = doc(db, 'videos', id);
      await setDoc(videoRef, {
        videoId: id,
        title,
        language: targetLanguage,
        url: `https://www.youtube.com/watch?v=${id}`,
        addedAt: serverTimestamp(),
        addedBy: 'annotator',
      }, { merge: true });
    } catch (e) {
      console.error('Error saving video document:', e);
    }
  };

  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const handleVideoClear = () => {
    setVideoId(null);
    setVideoTitle('');
    setCurrentTime(0);
    setSelectedSubtitle(null);
    setAnnotationMode(false);
    setLastSavedAnnotation(null);
  };

  // --- Subtitle handlers ---
  const handleSubtitleSelect = (subtitle) => {
    setSelectedSubtitle(subtitle);
    setAnnotationMode(false);
    setLastSavedAnnotation(null);
  };

  // --- Word selection handlers ---
  // The annotation form searches yasna for the selected text straight away,
  // so a word that's already in the dictionary is one click from linked.
  const handleSelectionReady = (text, indices) => {
    setSurfaceText(text);
    setWordIndices(indices);
    setAnnotationMode(true);
    setLastSavedAnnotation(null);
  };

  const handleExistingAnnotation = (annotation) => {
    // Could fetch the full entry and show cards — for now just log
    console.log('Existing annotation found:', annotation);
  };

  // --- Annotation handlers ---
  const handleAnnotationSaved = (annotation) => {
    setLastSavedAnnotation(annotation);
    setAnnotationMode(false);
    // Remount WordSelector so the selection clears after save
    setWordSelectorKey((k) => k + 1);
  };

  const handleAnnotationCancel = () => {
    setAnnotationMode(false);
  };

  return (
    <TimelineProvider value={timeline}>
    <div className="page-shell">
      <div className="page-shell-inner annotate-page">

        {/* Step 1: Video */}
        <MediaPlayer
          defaultVideoUrl={DEFAULT_MEDIA_URL}
          onVideoLoad={handleVideoLoad}
          onTimeUpdate={handleTimeUpdate}
          onVideoClear={handleVideoClear}
          titleContent={videoId && (
            // The title's meaning and words, shown under the title while the
            // video is expanded (Requirement 45)
            <TitlePanel
              videoId={videoId}
              title={videoTitle}
              language={targetLanguage}
              yasna={yasna}
              yasnaError={yasnaError}
              titleSubtitle={content.titleSubtitle}
              onTitleSubtitleSaved={content.setTitleSubtitle}
              annotations={content.titleAnnotations}
              onSaved={content.upsertTitleAnnotation}
              onUpdated={content.upsertTitleAnnotation}
              onDeleted={content.removeTitleAnnotation}
            />
          )}
        />

        {/* Step 2: What is showing now — the current subtitle and
            annotations, with edit and delete */}
        {videoId && (
          <CurrentMoment
            videoId={videoId}
            currentTime={currentTime}
            subtitles={content.subtitles}
            annotations={content.annotations}
            language={targetLanguage}
            yasna={yasna}
            yasnaError={yasnaError}
            onSubtitleSelect={handleSubtitleSelect}
            onSubtitleEdited={onSubtitleEdited}
            onSubtitleDeleted={onSubtitleDeleted}
            onAnnotationSaved={content.upsertAnnotation}
            onAnnotationUpdated={content.upsertAnnotation}
            onAnnotationDeleted={content.removeAnnotation}
          />
        )}

        {/* Steps 3 and 4: the Add panel (always open, one shared time window)
            and all the subtitles with their annotations (collapsed), in the
            order the teacher chose with their up / down arrows. */}
        {videoId && order.map((key) => (
          <React.Fragment key={key}>
            {key === 'add' ? (
              <AddPanel
                videoId={videoId}
                currentTime={currentTime}
                language={targetLanguage}
                yasna={yasna}
                yasnaError={yasnaError}
                onSubtitleAdded={content.upsertSubtitle}
                onAnnotationSaved={content.upsertAnnotation}
                moveControl={<MoveSection sectionKey="add" order={order} move={moveSection} />}
              />
            ) : (
              <BunchList
                videoId={videoId}
                currentTime={currentTime}
                language={targetLanguage}
                yasna={yasna}
                yasnaError={yasnaError}
                subtitles={content.subtitles}
                annotations={content.annotations}
                loading={content.loading}
                onSubtitleSelect={handleSubtitleSelect}
                onSubtitleEdited={onSubtitleEdited}
                onSubtitleDeleted={onSubtitleDeleted}
                onAnnotationSaved={content.upsertAnnotation}
                onAnnotationUpdated={content.upsertAnnotation}
                onAnnotationDeleted={content.removeAnnotation}
                moveControl={<MoveSection sectionKey="list" order={order} move={moveSection} />}
              />
            )}
          </React.Fragment>
        ))}

        {/* Word selection in a listed subtitle (visible once one is selected) */}
        {selectedSubtitle && (
          <div className="annotate-section">
            <WordSelector
              key={wordSelectorKey}
              subtitle={selectedSubtitle}
              onSelectionReady={handleSelectionReady}
              onExistingAnnotation={handleExistingAnnotation}
            />
          </div>
        )}

        {/* Annotation form for a subtitle selection */}
        {annotationMode && selectedSubtitle && (
          <AnnotationForm
            key={`${surfaceText}|${wordIndices.join(',')}`}
            surfaceText={surfaceText}
            wordIndices={wordIndices}
            subtitle={selectedSubtitle}
            language={targetLanguage}
            onSaved={handleAnnotationSaved}
            onCancel={handleAnnotationCancel}
          />
        )}

        {/* Success message after saving */}
        {lastSavedAnnotation && (
          <div className="annotate-success">
            Annotation saved: "{lastSavedAnnotation.surfaceText}"
          </div>
        )}
      </div>
    </div>
    </TimelineProvider>
  );
}

export default AnnotatePage;
